import React, { useState } from "react";
import "./CustomizationOptions.css";

function CustomizationOptions({
  onSave,
  onCancel,
  initialCustomization = null,
}) {
  const [dressLength, setDressLength] = useState(
    initialCustomization?.dressLength || "Standard"
  );

  const [sleeves, setSleeves] = useState(
    initialCustomization?.sleeves || "Short"
  );

  const [neckStyle, setNeckStyle] = useState(
    initialCustomization?.neckStyle || "Round"
  );

  const [fit, setFit] = useState(
    initialCustomization?.fit || "Regular"
  );

  const [customDressLength, setCustomDressLength] = useState(
    initialCustomization?.customDressLength || ""
  );

  const [customSleeves, setCustomSleeves] = useState(
    initialCustomization?.customSleeves || ""
  );

  const [customNeckStyle, setCustomNeckStyle] = useState(
    initialCustomization?.customNeckStyle || ""
  );

  const [specialInstructions, setSpecialInstructions] = useState(
    initialCustomization?.specialInstructions || ""
  );

  const [referenceImage, setReferenceImage] = useState(
    initialCustomization?.referenceImage || null
  );

  // =========================================
  // EXTRA PRICE
  // =========================================

  const calculateExtraPrice = () => {
    let extraPrice = 0;

    if (dressLength === "Custom") {
      extraPrice += 10;
    }

    if (sleeves === "Custom") {
      extraPrice += 10;
    }

    if (neckStyle === "Custom") {
      extraPrice += 10;
    }

    return extraPrice;
  };

  // =========================================
  // REFERENCE IMAGE
  // =========================================

  const handleReferenceImage = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("Please select an image smaller than 5 MB.");
      return;
    }

    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image file.");
      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      setReferenceImage({
        name: file.name,
        type: file.type,
        size: file.size,
        data: reader.result,
      });
    };

    reader.readAsDataURL(file);
  };

  // =========================================
  // SAVE CUSTOMIZATION
  // =========================================

  const handleSave = () => {
    if (
      dressLength === "Custom" &&
      !customDressLength.trim()
    ) {
      alert("Please enter your custom dress length.");
      return;
    }

    if (
      sleeves === "Custom" &&
      !customSleeves.trim()
    ) {
      alert("Please enter your custom sleeve requirement.");
      return;
    }

    if (
      neckStyle === "Custom" &&
      !customNeckStyle.trim()
    ) {
      alert("Please enter your custom neck style.");
      return;
    }

    const customizationData = {
      dressLength,

      customDressLength:
        dressLength === "Custom"
          ? customDressLength.trim()
          : "",

      sleeves,

      customSleeves:
        sleeves === "Custom"
          ? customSleeves.trim()
          : "",

      neckStyle,

      customNeckStyle:
        neckStyle === "Custom"
          ? customNeckStyle.trim()
          : "",

      fit,

      specialInstructions:
        specialInstructions.trim(),

      referenceImage,

      extraPrice: calculateExtraPrice(),
    };

    onSave(customizationData);
  };

  return (
    <div className="customization-options">

      {/* =========================================
          CLOSE BUTTON
      ========================================= */}

      <button
        type="button"
        className="customization-close-btn"
        onClick={onCancel}
        aria-label="Close customization options"
      >
        ×
      </button>


      {/* =========================================
          HEADER
      ========================================= */}

      <div className="customization-header">

        <div>
          <p className="customization-small-title">
            PERSONALISE YOUR OUTFIT
          </p>

          <h3>
            Customisation Options
          </h3>
        </div>

        <span className="customization-badge" id="outfit">
          ✨ Customise This Outfit
        </span>

      </div>


      {/* =========================================
          DRESS LENGTH
      ========================================= */}

      <div className="customization-section">

        <label className="customization-label">
          👗 Dress Length
        </label>

        <div className="customization-option-grid">

          {[
            "Standard",
            "Knee Length",
            "Midi",
            "Ankle Length",
            "Custom",
          ].map((option) => (

            <button
              type="button"
              key={option}
              className={`customization-option-btn ${
                dressLength === option
                  ? "active"
                  : ""
              }`}
              onClick={() =>
                setDressLength(option)
              }
            >
              {option}
            </button>

          ))}

        </div>

        {dressLength === "Custom" && (

          <input
            type="text"
            className="customization-text-input"
            placeholder="Example: 48 inches"
            value={customDressLength}
            onChange={(e) =>
              setCustomDressLength(e.target.value)
            }
          />

        )}

      </div>


      {/* =========================================
          SLEEVES
      ========================================= */}

      <div className="customization-section">

        <label className="customization-label">
          👚 Sleeves
        </label>

        <div className="customization-option-grid">

          {[
            "Sleeveless",
            "Short",
            "3/4",
            "Full",
            "Custom",
          ].map((option) => (

            <button
              type="button"
              key={option}
              className={`customization-option-btn ${
                sleeves === option
                  ? "active"
                  : ""
              }`}
              onClick={() =>
                setSleeves(option)
              }
            >
              {option}
            </button>

          ))}

        </div>

        {sleeves === "Custom" && (

          <input
            type="text"
            className="customization-text-input"
            placeholder="Example: Elbow length sleeves"
            value={customSleeves}
            onChange={(e) =>
              setCustomSleeves(e.target.value)
            }
          />

        )}

      </div>


      {/* =========================================
          NECK STYLE
      ========================================= */}

      <div className="customization-section">

        <label className="customization-label">
          💎 Neck Style
        </label>

        <div className="customization-option-grid">

          {[
            "Round",
            "V-Neck",
            "Square",
            "High Neck",
            "Custom",
          ].map((option) => (

            <button
              type="button"
              key={option}
              className={`customization-option-btn ${
                neckStyle === option
                  ? "active"
                  : ""
              }`}
              onClick={() =>
                setNeckStyle(option)
              }
            >
              {option}
            </button>

          ))}

        </div>

        {neckStyle === "Custom" && (

          <input
            type="text"
            className="customization-text-input"
            placeholder="Example: Sweetheart neck"
            value={customNeckStyle}
            onChange={(e) =>
              setCustomNeckStyle(e.target.value)
            }
          />

        )}

      </div>


      {/* =========================================
          FIT
      ========================================= */}

      <div className="customization-section">

        <label className="customization-label">
          ✨ Fit
        </label>

        <div className="customization-option-grid fit-grid">

          {[
            "Regular",
            "Slim",
            "Loose",
          ].map((option) => (

            <button
              type="button"
              key={option}
              className={`customization-option-btn ${
                fit === option
                  ? "active"
                  : ""
              }`}
              onClick={() =>
                setFit(option)
              }
            >
              {option}
            </button>

          ))}

        </div>

      </div>


      {/* =========================================
          SPECIAL INSTRUCTIONS
      ========================================= */}

      <div className="customization-section">

        <label className="customization-label">
          📝 Special Instructions
        </label>

        <textarea
          className="customization-textarea"
          rows="4"
          maxLength="500"
          placeholder="Example: Please keep the waist slightly loose..."
          value={specialInstructions}
          onChange={(e) =>
            setSpecialInstructions(e.target.value)
          }
        />

        <div className="customization-character-count">
          {specialInstructions.length}/500
        </div>

      </div>


      {/* =========================================
          REFERENCE IMAGE
      ========================================= */}

      <div className="customization-section">

        <label className="customization-label">
          📷 Reference Image
        </label>

        <p className="customization-help-text">
          Upload a reference image if you want a
          particular style, sleeve, neck or fitting.
        </p>

        <label className="customization-upload">

          <input
            type="file"
            accept="image/*"
            onChange={handleReferenceImage}
          />

          <span>
            📷 Choose Reference Image
          </span>

        </label>

        {referenceImage && (

          <div className="customization-image-preview">

            <img
              src={referenceImage.data}
              alt="Customization reference"
            />

            <div className="customization-image-info">

              <strong>
                {referenceImage.name}
              </strong>

              <button
                type="button"
                onClick={() =>
                  setReferenceImage(null)
                }
              >
                Remove
              </button>

            </div>

          </div>

        )}

      </div>


      {/* =========================================
          PRICE
      ========================================= */}

      <div className="customization-price-box">

        <span>
          Customisation Cost
        </span>

        <strong>
          {calculateExtraPrice() > 0
            ? `+ £${calculateExtraPrice().toFixed(2)}`
            : "FREE"}
        </strong>

      </div>


      {/* =========================================
          ACTION BUTTONS
      ========================================= */}

      <div className="customization-actions">

        <button
          type="button"
          className="customization-cancel-btn"
          onClick={onCancel}
        >
          Cancel
        </button>

        <button
          type="button"
          className="customization-save-btn"
          onClick={handleSave}
        >
          Save Customisation
        </button>

      </div>

    </div>
  );
}

export default CustomizationOptions;
